import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

interface IProps {
  value: string;
  onChange: (value: string) => void;
}

const WysiwygEditor: React.FC<IProps> = ({ value, onChange }) => {
  const quillRef = useRef(null);
  const [initTitlesDone, setInitTitlesDone] = useState(false);
  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ font: [] }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"], // toggled buttons
          ["blockquote", "code-block"],
          [{ color: [] }, { background: [] }], // dropdown with defaults from theme
          [{ list: "ordered" }, { list: "bullet" }, { align: [] }],
          [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
          ["link", "image"],
          ["clean"], // remove formatting button
        ],
      },
      clipboard: { matchVisual: false },
    }),
    []
  );

  useEffect(() => {
    if (initTitlesDone) return;
    if (quillRef.current) {
      const setTitle = (
        parent: HTMLElement,
        element: string,
        title: string,
        all?: boolean
      ) => {
        if (all) {
          parent
            .querySelectorAll(element)
            ?.forEach((el) => el.setAttribute("title", title));
        } else {
          parent.querySelector(element)?.setAttribute("title", title);
        }
      };

      const toolbar = (quillRef.current as any).getEditor().container
        .previousSibling;
      if (toolbar) {
        setTitle(toolbar, "span.ql-font", "Font family");
        setTitle(toolbar, "span.ql-header", "Font size");
        setTitle(toolbar, "button.ql-bold", "Bold");
        setTitle(toolbar, "button.ql-italic", "Italic");
        setTitle(toolbar, "button.ql-underline", "Underline");
        setTitle(toolbar, "button.ql-strike", "Strikethrough");
        setTitle(toolbar, "button.ql-blockquote", "Block quote");
        setTitle(toolbar, "button.ql-code-block", "Code block");
        setTitle(toolbar, "span.ql-color", "Font color");
        setTitle(toolbar, "span.ql-background", "Background color");
        setTitle(toolbar, "button.ql-list", "List", true);
        setTitle(toolbar, "span.ql-align", "Align");
        setTitle(toolbar, "button.ql-indent", "Indent", true);
        setTitle(toolbar, "button.ql-link", "Link");
        setTitle(toolbar, "button.ql-image", "Image");
        setTitle(toolbar, "button.ql-clean", "Clear Formatting");
        setInitTitlesDone(true);
      }
    }
  }, [quillRef, initTitlesDone]);

  return (
    <ReactQuill
      ref={quillRef}
      modules={quillModules}
      value={value}
      theme="snow"
      onChange={onChange}
    />
  );
};

export default React.memo(WysiwygEditor, (prevProps, nextProps) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.onChange === nextProps.onChange
  );
});
